// Browser-side image compression shared by photo-upload and offline-queue
// callers, including dailyPhotos.ts and tasksAdminApi.ts.
//
// Compression defaults are exported below and pinned by photoCompress.test.ts:
//   max-edge 1024px, JPEG quality 0.7, ~80KB target.
//
// Why a thin wrapper now: the canvas + Image roundtrip is awkward in
// jsdom — vitest covers signature + error paths only here. Real bytes-in
// / bytes-out behavior is verified in Phase 2 Playwright when an actual
// browser canvas is in scope.

export type PhotoCompressOptions = {
  maxEdge?: number;
  quality?: number;
  targetBytes?: number;
  mimeType?: string;
};

type DecodedBitmap = ImageBitmap | HTMLImageElement;

type CanvasLike = {
  width: number;
  height: number;
  getContext: (contextId: '2d') => CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  convertToBlob?: (options: {type: string; quality: number}) => Promise<Blob>;
  toBlob?: (callback: (blob: Blob | null) => void, type?: string, quality?: number) => void;
};

const DEFAULTS = Object.freeze({
  maxEdge: 1024,
  quality: 0.7,
  // targetBytes is advisory; quality is the hard knob. We expose it so a
  // future caller can iterate quality down if a single pass blows past
  // the target — keep the API stable for that.
  targetBytes: 80_000,
  mimeType: 'image/jpeg',
});

export const PHOTO_COMPRESS_DEFAULTS = DEFAULTS;

function isBlobLike(v: unknown): v is Blob {
  if (v == null || typeof v !== 'object') return false;
  const candidate = v as {size?: unknown; type?: unknown};
  return typeof candidate.size === 'number' && typeof candidate.type === 'string';
}

/**
 * Compress an image blob into a smaller JPEG blob.
 *
 * @param {Blob | File} blob — source image (any browser-decodable format).
 * @param {object} [opts]
 * @param {number} [opts.maxEdge=1024] — longest-edge px cap.
 * @param {number} [opts.quality=0.7] — JPEG quality 0..1.
 * @param {string} [opts.mimeType='image/jpeg'] — output MIME.
 * @returns {Promise<Blob>}
 */
export async function compressImage(blob: Blob | File, opts: PhotoCompressOptions = {}): Promise<Blob> {
  if (!isBlobLike(blob)) {
    throw new TypeError('compressImage: first argument must be a Blob or File');
  }
  const {maxEdge, quality, mimeType} = {...DEFAULTS, ...opts};
  if (!(quality > 0 && quality <= 1)) {
    throw new RangeError('compressImage: quality must be in (0, 1]');
  }
  if (!(maxEdge > 0)) {
    throw new RangeError('compressImage: maxEdge must be > 0');
  }

  // Decode → resize via canvas → re-encode. Browser-only path; jsdom
  // doesn't ship canvas/Image so unit tests stub at the call boundary.
  const bitmap = await loadBitmap(blob);
  try {
    const {width, height} = fitWithin(bitmap.width, bitmap.height, maxEdge);
    const canvas = makeCanvas(width, height);
    // getContext('2d') returns NULL rather than throwing when the context
    // cannot be created — a canvas larger than the device's surface limit, or
    // memory pressure on a low-end phone. Dereferencing it produced a bare
    // "Cannot read properties of null (reading 'drawImage')", which reaches the
    // upload UI as an unactionable failure. The bitmap is still released: the
    // finally block below runs on this throw.
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error(
        'compressImage: could not obtain a 2D canvas context — the photo may be too large for this device',
      );
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    return await canvasToBlob(canvas, mimeType, quality);
  } finally {
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

function fitWithin(w: number, h: number, maxEdge: number): {width: number; height: number} {
  if (w <= maxEdge && h <= maxEdge) return {width: w, height: h};
  const scale = w >= h ? maxEdge / w : maxEdge / h;
  return {width: Math.round(w * scale), height: Math.round(h * scale)};
}

async function loadBitmap(blob: Blob): Promise<DecodedBitmap> {
  // Prefer createImageBitmap (faster, off-thread on supporting browsers).
  if (typeof globalThis.createImageBitmap === 'function') {
    return await globalThis.createImageBitmap(blob);
  }
  // Fallback via Image + object URL.
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('compressImage: image decode failed'));
      i.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function makeCanvas(w: number, h: number): CanvasLike {
  if (typeof OffscreenCanvas === 'function') return new OffscreenCanvas(w, h) as unknown as CanvasLike;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

async function canvasToBlob(canvas: CanvasLike, mime: string, quality: number): Promise<Blob> {
  // OffscreenCanvas exposes convertToBlob; HTMLCanvasElement uses toBlob.
  if (typeof canvas.convertToBlob === 'function') {
    return await canvas.convertToBlob({type: mime, quality});
  }
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob!(
      (b) => (b ? resolve(b) : reject(new Error('compressImage: canvas.toBlob returned null'))),
      mime,
      quality,
    );
  });
}
